/**
 * Redux slice for Voice Leads admin dashboard.
 */
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchVoiceLeads,
  fetchVoiceLeadStats,
  fetchVoiceLeadQuality,
  fetchVoiceLeadDetail,
  fetchVoiceLeadEvents,
  triggerVoiceLeadRetry,
} from '../actions/voiceLeadsActions';

const initialState = {
  // List
  leads: [],
  total: 0,
  page: 1,
  limit: 50,
  loading: false,
  error: null,
  errorStatus: null,
  errorKind: null,
  leadsStale: false,

  // Stats
  stats: null,
  statsLoading: false,
  statsError: null,
  statsErrorStatus: null,
  statsErrorKind: null,
  statsStale: false,
  quality: null,
  qualityLoading: false,
  qualityError: null,
  qualityErrorStatus: null,
  qualityErrorKind: null,
  qualityStale: false,

  // Detail
  currentLead: null,
  currentCallAttempts: [],
  detailLoading: false,
  detailError: null,

  // Events
  events: [],
  eventsLoading: false,
  eventsError: null,
};

const voiceLeadsSlice = createSlice({
  name: 'voiceLeads',
  initialState,
  reducers: {
    clearCurrentLead: (state) => {
      state.currentLead = null;
      state.currentCallAttempts = [];
      state.events = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch leads list
    builder.addCase(fetchVoiceLeads.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.errorStatus = null;
      state.errorKind = null;
    });
    builder.addCase(fetchVoiceLeads.fulfilled, (state, action) => {
      state.loading = false;
      state.leads = Array.isArray(action.payload?.leads) ? action.payload.leads : [];
      state.total = Number(action.payload?.total || 0);
      state.page = action.payload?.page || 1;
      state.limit = action.payload?.limit || state.limit;
      state.leadsStale = false;
      state.error = null;
      state.errorStatus = null;
      state.errorKind = null;
    });
    builder.addCase(fetchVoiceLeads.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.error || 'Failed to fetch leads';
      state.errorStatus = action.payload?.status || null;
      state.errorKind = action.payload?.kind || 'fallback';
      state.leadsStale = state.leads.length > 0;
    });

    // Fetch stats
    builder.addCase(fetchVoiceLeadStats.pending, (state) => {
      state.statsLoading = true;
      state.statsError = null;
      state.statsErrorStatus = null;
      state.statsErrorKind = null;
    });
    builder.addCase(fetchVoiceLeadStats.fulfilled, (state, action) => {
      state.statsLoading = false;
      state.stats = action.payload;
      state.statsError = null;
      state.statsErrorStatus = null;
      state.statsErrorKind = null;
      state.statsStale = false;
    });
    builder.addCase(fetchVoiceLeadStats.rejected, (state, action) => {
      state.statsLoading = false;
      state.statsError = action.payload?.error || 'Failed to fetch stats';
      state.statsErrorStatus = action.payload?.status || null;
      state.statsErrorKind = action.payload?.kind || 'fallback';
      state.statsStale = Boolean(state.stats);
    });

    // Fetch quality telemetry
    builder.addCase(fetchVoiceLeadQuality.pending, (state) => {
      state.qualityLoading = true;
      state.qualityError = null;
      state.qualityErrorStatus = null;
      state.qualityErrorKind = null;
    });
    builder.addCase(fetchVoiceLeadQuality.fulfilled, (state, action) => {
      state.qualityLoading = false;
      state.quality = action.payload;
      state.qualityError = null;
      state.qualityErrorStatus = null;
      state.qualityErrorKind = null;
      state.qualityStale = false;
    });
    builder.addCase(fetchVoiceLeadQuality.rejected, (state, action) => {
      state.qualityLoading = false;
      state.qualityError = action.payload?.error || 'Failed to fetch quality metrics';
      state.qualityErrorStatus = action.payload?.status || null;
      state.qualityErrorKind = action.payload?.kind || 'fallback';
      state.qualityStale = Boolean(state.quality);
    });

    // Fetch detail
    builder.addCase(fetchVoiceLeadDetail.pending, (state) => {
      state.detailLoading = true;
      state.detailError = null;
    });
    builder.addCase(fetchVoiceLeadDetail.fulfilled, (state, action) => {
      state.detailLoading = false;
      state.currentLead = action.payload.lead;
      state.currentCallAttempts = action.payload.callAttempts;
    });
    builder.addCase(fetchVoiceLeadDetail.rejected, (state, action) => {
      state.detailLoading = false;
      state.detailError = action.payload?.error || 'Failed to fetch lead detail';
    });

    // Fetch events
    builder.addCase(fetchVoiceLeadEvents.pending, (state) => {
      state.eventsLoading = true;
      state.eventsError = null;
    });
    builder.addCase(fetchVoiceLeadEvents.fulfilled, (state, action) => {
      state.eventsLoading = false;
      state.events = action.payload.events;
    });
    builder.addCase(fetchVoiceLeadEvents.rejected, (state, action) => {
      state.eventsLoading = false;
      state.eventsError = action.payload?.error || 'Failed to fetch events';
    });

    // Trigger retry (just refresh the lead data after)
    builder.addCase(triggerVoiceLeadRetry.fulfilled, () => {
      // Will be handled by component refetch
    });
  },
});

export const { clearCurrentLead } = voiceLeadsSlice.actions;
export default voiceLeadsSlice.reducer;
